import { ReactNode, useState } from "react";
import { TRACK_COLORS } from "config/constants";
import { Routing } from "config/enums/routings";
import { CircleMarker, useMap } from "react-leaflet";
import { useRouting } from "stores/routingStore/RoutingContext";
import { Coords } from "types/app";

/**
 * Above the track line, the hotline (z-index 410) and the markers (600), whose point would hide it (every point
 * of a track without routing is a marker), below the tooltips (650). It doesn't take mouse events, so the markers
 * under it can still be clicked and dragged.
 */
export const TRACK_POINT_MARKER_PANE = "trackPointMarkerPane";

const getPane = (map: ReturnType<typeof useMap>) => {
  const existing = map.getPane(TRACK_POINT_MARKER_PANE);
  if (existing) return existing;
  const pane = map.createPane(TRACK_POINT_MARKER_PANE);
  pane.style.zIndex = "620";
  pane.style.pointerEvents = "none";
  return pane;
};

/**
 * A point of the edited track: a white circle with a ring in the track's color and a soft shadow.
 * Shown where the track line or the chart is hovered. `children` are bound to it (e.g. a tooltip).
 */
export const TrackPointMarker = ({
  position,
  children,
}: {
  position: Coords;
  children?: ReactNode;
}) => {
  const map = useMap();
  // Created once, shared by every marker (two <Pane> components with the same name would throw)
  useState(() => getPane(map));
  const {
    state: { routing, trackColor },
  } = useRouting();
  const color =
    trackColor ||
    (routing === Routing.none
      ? TRACK_COLORS.nonRoutable
      : TRACK_COLORS.routable);

  return (
    <>
      <CircleMarker
        center={position}
        radius={10}
        interactive={false}
        pane={TRACK_POINT_MARKER_PANE}
        pathOptions={{ stroke: false, fillColor: "#000", fillOpacity: 0.2 }}
      />
      <CircleMarker
        center={position}
        radius={7}
        interactive={false}
        pane={TRACK_POINT_MARKER_PANE}
        pathOptions={{
          color,
          weight: 3,
          opacity: 1,
          fillColor: "#fff",
          fillOpacity: 1,
        }}
      >
        {children}
      </CircleMarker>
    </>
  );
};
